<template>
  <div class="o-ring-design-space-assistant__container">
    <div class="o-ring-design-space-assistant__container__headline">
      <h1 class="h1--underline">
        {{ i18n.tv('Frontend.ORing.DesignSpaceAssis', 'ORingConfiguratorImport') }}
      </h1>
      <NavigationClose @click="showPopup=true" />
    </div>
    <div class="o-ring-design-space-assistant__content">
      <ORingDesignSpaceAssistantSteps />
    </div>
    <LeaveConfirmationPopup
      :show="showPopup"
      :size="POPUP.Size.small"
      :title="i18n.tv('discardPromptTitle', 'ORingConfigurator')"
      :content="i18n.tv('Frontend.Wizard.LeaveDescription', 'ORingConfiguratorImport')"
      :stay-button-text="i18n.tv('Frontend.Wizard.Stay', 'ORingConfiguratorImport')"
      :leave-button-text="i18n.tv('Frontend.Wizard.Leave', 'ORingConfiguratorImport')"
      @update:show="showPopup = $event"
      @leave="closeORingAssistant"
    />
  </div>
</template>

<script setup lang="ts">
import useBreadcrumb from '~/composables/useBreadcrumb'
import useI18nExtended from '~/composables/usei18nExtended'
import NavigationClose from '~/assets/icons/navigation_close.svg?component'
import {POPUP} from '~/assets/js/constants'
import ORingDesignSpaceAssistantSteps from '~/components/oring-calculator/ORingDesignSpaceAssistantSteps.vue'
import LeaveConfirmationPopup from '~/components/popup/LeaveConfirmationPopup.vue'
import createSeoMetaInfo from '~/assets/js/opengraph/helper'
import {getORingSeoOptions} from '~/composables/oring-helper'
import {useORingCalculatorStore} from '~/store/oring-calculator'
import {useOringScrollToTop} from '~/composables/oring/useOringScrollToTop'
import {useOringNavigationGuard} from '~/composables/oring/useOringNavigationGuard'

// Add SEO metadata
const seoData = createSeoMetaInfo(getORingSeoOptions('design-space-assistant', 'Frontend.ORing.DesignSpaceAssis', 'ORingConfiguratorImport'))
useSeoMeta(seoData)

const oringStore = useORingCalculatorStore()
const i18n = useI18nExtended()
const route = useRoute()
const router = useRouter()
let i18nHead = useLocaleHead({addSeoAttributes: true, route: route, router: router, i18n: i18n}).value

const head = {
  ...i18nHead,
  title: i18n.tv('pageTitle', 'ORingConfigurator')
}
useHead(head)

const showPopup = ref(false)
const {scrollToTop} = useOringScrollToTop()
useBreadcrumb([
  {
    label: i18n.tv('oRingCalculator', 'breadcrumbs'),
    path: '/oring-calculator'
  },
  {
    label: i18n.tv('Frontend.ORing.DesignSpaceAssis', 'ORingConfiguratorImport'),
    path: '/oring-calculator/design-space-assistant'
  }
])

const app = useNuxtApp()

const {confirmLeave, clearPendingRoute} = useOringNavigationGuard(
  router,
  showPopup,
  '/oring-calculator/design-space-assistant',
  ['/oring-calculator/design-cockpit']
)

const closeORingAssistant = () => {
  showPopup.value = false
  oringStore.resetDesignSpaceStates()

  const pendingRoute = confirmLeave()
  if (pendingRoute) {
    router.push(pendingRoute)
    clearPendingRoute()
  } else {
    navigateTo(app.$globalization.localePath('/oring-calculator'))
  }
}

onMounted(() => {
  scrollToTop('auto')
})

</script>
<style lang="scss">
.o-ring-design-space-assistant__container {
  background-color: $color__white;
  margin-bottom: 1.5rem;

  .o-ring-design-space-assistant__content {
    padding: 0 2rem;
    margin-top: 1.25rem;

    @include breakpoint-down($md) {
      padding: 0 1rem;
    }
  }

  &__headline {
    display: flex;
    padding: 0 2rem 2rem;
    justify-content: space-between;
    color: $color__white;
    padding-top: .5rem;

    @include breakpoint-down($md) {
      padding: 0 1rem 1rem;
    }

    .icon.icon--svg {
      font-size: 2rem;
      fill: $color__fst__primary;
      cursor: pointer;
    }

    h1 {
      margin:0;
    }
  }

  .popup-content {
    padding: 1rem 0 0;
    font-weight: $base-text-font-weight-light;

    .popup-buttons {
      display: flex;
      justify-content: flex-end;
      gap: 1rem;
      padding-top: 1rem;
    }
  }

  h5 {
    font-size: 1.125rem;
    margin: -.5rem 0 0;
  }
}
</style>
